// Guest chat inbox on the Restaurant OS (docs/GUEST_CHAT_2026-10-28.md):
// guests write from the restaurant's public pages; whoever holds chat.reply
// (every POS role, owner Oct 29 2026) answers here. The list and the open chat are
// live (restaurant events chat.*), a new guest message chimes, opening a chat
// marks it read for everyone. "Book table" uses the restaurant's own booking
// rules (POST /api/dining/chat/conversations/{id}/reservation; chat.reply is enough). Every message
// is rendered as text.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText, newKey } from '../lib/api';
import { CHAT_EVENTS } from '../lib/chat';
import { clock, initials, secondsSince, todayYmd } from '../lib/format';
import { useDiningSession } from '../lib/session';
import { chime, useSoundPref } from '../lib/sound';
import { useLive } from '../lib/useLive';
import { C } from '../theme/tokens';
import { BottomNav, DarkTopBar, UserMenu } from '../components/Shell';
import { AlertBell, AlertCenter, alertsEnabledFor, useAlerts } from '../components/AlertCenter';
import { Avatar, Btn, Empty, ErrorNote, Field, IconBtn, LiveIndicator, Loading, Modal, ModalFoot, ModalHead, Segmented, StreamBanner, inputStyle, useNow, useToast } from '../components/ui';
import { IconCalendar, IconChat, IconCheck, IconPhone, IconSend, IconSoundOff, IconSoundOn } from '../components/icons';

function ago(iso, now) {
  const s = secondsSince(iso, now);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function requestLine(r) {
  if (!r) return null;
  return `${r.party_size} ${r.party_size === 1 ? 'guest' : 'guests'} · ${r.date} · ${r.time}`;
}

function smsLabel(status) {
  return { sent: 'Texted to the guest', failed: 'Text could not be sent', sending: 'Texting the guest…' }[status] || null;
}

function Row({ c, on, onOpen, now }) {
  const last = c.last_message;
  return (
    <button type="button" onClick={() => onOpen(c.id)} aria-current={on ? 'true' : undefined}
      style={{ width: '100%', textAlign: 'left', border: 'none', borderRadius: 16, padding: 12, display: 'flex', gap: 12, alignItems: 'flex-start', background: on ? C.line2 : 'transparent', color: C.text }}>
      <Avatar text={initials(c.guest_name)} size={42} ring={c.unread ? C.orange : undefined} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: c.unread ? 800 : 600, color: C.textHi, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.guest_name}</span>
          <span style={{ fontSize: 12, color: C.textLo, flex: 'none' }}>{ago(c.last_message_at, now)}</span>
        </div>
        {c.topic === 'reservation' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: c.reservation_id ? C.greenText : '#FDBA74' }}>
            <IconCalendar size={14} />{c.reservation_id ? 'Table booked' : requestLine(c.reservation_request) || 'Table request'}
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: c.unread ? C.text : C.textLo, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {last ? `${last.sender === 'staff' ? 'You: ' : ''}${last.preview}` : ''}
          </span>
          {c.unread > 0 && <span style={{ minWidth: 22, height: 22, padding: '0 6px', borderRadius: 11, background: C.orange, color: C.orangeDeep, fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{c.unread}</span>}
        </div>
      </div>
    </button>
  );
}

function Message({ m }) {
  if (m.sender === 'system') {
    const booked = m.kind === 'reservation_created';
    return (
      <div style={{ alignSelf: 'center', maxWidth: '86%', padding: booked ? '10px 14px' : '6px 12px', borderRadius: booked ? 14 : 999, fontSize: 13, fontWeight: 600, textAlign: 'center',
        background: booked ? 'rgba(34,197,94,.14)' : C.panel2, color: booked ? C.greenText : C.textLo, border: `1px solid ${booked ? 'rgba(34,197,94,.3)' : C.line}`, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {booked && <IconCheck size={14} />} {m.body}
        {m.author_name && <div style={{ fontSize: 11.5, fontWeight: 500, marginTop: 2, color: C.textLo }}>by {m.author_name}{smsLabel(m.sms_status) ? ` · ${smsLabel(m.sms_status)}` : ''}</div>}
      </div>
    );
  }
  const staff = m.sender === 'staff';
  return (
    <div style={{ alignSelf: staff ? 'flex-end' : 'flex-start', maxWidth: '76%', display: 'flex', flexDirection: 'column', alignItems: staff ? 'flex-end' : 'flex-start', gap: 4 }}>
      <div style={{ padding: '10px 14px', borderRadius: 18, borderBottomRightRadius: staff ? 6 : 18, borderBottomLeftRadius: staff ? 18 : 6, fontSize: 15, lineHeight: 1.45,
        background: staff ? C.orange : '#FFFFFF', color: staff ? C.orangeDeep : C.ink, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {m.kind === 'reservation_request' && <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 800, color: C.orangeInk, marginBottom: 4 }}><IconCalendar size={14} />Table request</div>}
        {m.body}
      </div>
      <span style={{ fontSize: 11.5, color: C.textLo }}>
        {[staff ? m.author_name : null, clock(m.created_at), staff ? smsLabel(m.sms_status) : null].filter(Boolean).join(' · ')}
      </span>
    </div>
  );
}

function BookModal({ open, onClose, conversation, onBooked }) {
  const toast = useToast();
  const asked = conversation?.reservation_request || {};
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open) return;
    setError(null);
    setV({
      party_size: asked.party_size || 2, date: asked.date || todayYmd(), time: asked.time || '19:00',
      customer_name: conversation?.guest_name || '', customer_phone: conversation?.guest_phone || '', special_requests: asked.note || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversation?.id]);
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: k === 'party_size' ? Number(e.target.value) : e.target.value }));
  const book = async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await api.post(`/dining/chat/conversations/${conversation.id}/reservation`, {
        ...v, special_requests: v.special_requests || null,
      });
      toast.ok(`Booked · ${answer.reservation.confirmation_code}${answer.reservation.table_number ? ` · Table ${answer.reservation.table_number}` : ''}`);
      onBooked();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} label="Book a table from this chat" width={520}>
      <ModalHead title="Book a table" sub="The restaurant's booking rules and a free table that fits, as for an online booking. The guest sees the confirmation in the chat." onClose={onClose} />
      <div style={{ padding: 24, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Guests"><input type="number" min={1} max={50} value={v.party_size || ''} onChange={set('party_size')} style={inputStyle} /></Field>
        <Field label="Date"><input type="date" value={v.date || ''} onChange={set('date')} style={inputStyle} /></Field>
        <Field label="Time"><input type="time" step={900} value={v.time || ''} onChange={set('time')} style={inputStyle} /></Field>
        <div style={{ gridColumn: '1 / span 3', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Name"><input value={v.customer_name || ''} maxLength={100} onChange={set('customer_name')} style={inputStyle} /></Field>
          <Field label="Phone" hint={conversation?.guest_phone ? null : 'Ask the guest for a phone number'}><input type="tel" value={v.customer_phone || ''} onChange={set('customer_phone')} style={inputStyle} /></Field>
        </div>
        <div style={{ gridColumn: '1 / span 3' }}>
          <Field label="Special requests"><input value={v.special_requests || ''} maxLength={500} onChange={set('special_requests')} style={inputStyle} /></Field>
        </div>
        {error && <ErrorNote light error={errorText(error)} style={{ gridColumn: '1 / span 3' }} />}
      </div>
      <ModalFoot>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn kind="orange" busy={busy} onClick={book} icon={<IconCalendar size={18} />}>Book table</Btn>
      </ModalFoot>
    </Modal>
  );
}

function Thread({ id, canBook, soundOn, onChanged }) {
  const toast = useToast();
  const now = useNow(30000);
  const live = useLive(() => api.get(`/dining/chat/conversations/${id}`), [id], {
    types: CHAT_EVENTS, match: (e) => Number(e.payload?.conversation_id) === Number(id),
  });
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState(false);
  const listRef = useRef(null);
  const conv = live.data?.conversation;
  const messages = useMemo(() => live.data?.messages || [], [live.data]);
  const lastGuest = messages.filter((m) => m.sender === 'guest').reduce((n, m) => Math.max(n, m.id), 0);
  const seenGuest = useRef(null);

  // opening the chat (or a new guest line while it is open) marks it read for everyone
  useEffect(() => {
    if (!conv || !conv.unread) return;
    if (document.visibilityState === 'hidden') return;
    api.post(`/dining/chat/conversations/${id}/read`).then(onChanged).catch(() => {});
  }, [id, conv, onChanged]);

  useEffect(() => {
    if (seenGuest.current !== null && lastGuest > seenGuest.current && soundOn) chime('new');
    seenGuest.current = lastGuest;
  }, [lastGuest, soundOn]);
  useEffect(() => { seenGuest.current = null; setDraft(''); }, [id]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(`/dining/chat/conversations/${id}/messages`, { body: text, client_id: newKey('chat') });
      setDraft('');
      live.reload();
      onChanged();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    try {
      await api.post(`/dining/chat/conversations/${id}/close`);
      toast.ok('Chat closed');
      live.reload();
      onChanged();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  if (!live.data && live.loading) return <Loading label="Opening chat…" />;
  if (live.error && !live.data) return <ErrorNote error={errorText(live.error)} onRetry={live.reload} style={{ margin: 24 }} />;
  if (!conv) return null;
  const contact = [conv.guest_phone, conv.guest_email].filter(Boolean).join(' · ');
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: `1px solid ${C.line}` }}>
        <Avatar text={initials(conv.guest_name)} size={44} />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: C.textHi, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{conv.guest_name}</span>
          <span style={{ fontSize: 13, color: C.textLo, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: conv.guest_here ? C.greenHi : C.textOff }} />
            {conv.guest_here ? 'On the page now' : `Last here ${conv.guest_last_seen_at ? ago(conv.guest_last_seen_at, now) : '—'} ago`}
            {contact && <><span>·</span><IconPhone size={13} />{contact}</>}
            {conv.sms_opt_in && <span>· gets replies by text when away</span>}
          </span>
        </div>
        {canBook && !conv.reservation && <Btn kind="dark" icon={<IconCalendar size={18} />} onClick={() => setBooking(true)}>Book table</Btn>}
        {conv.reservation && <span style={{ fontSize: 13, fontWeight: 700, color: C.greenText, display: 'inline-flex', gap: 6, alignItems: 'center' }}><IconCheck size={14} />{conv.reservation.confirmation_code} · {conv.reservation.date} {conv.reservation.time}</span>}
        {conv.status === 'open' && <Btn kind="dark" onClick={close}>Close chat</Btn>}
      </div>
      {conv.reservation_request && !conv.reservation && (
        <div style={{ flex: 'none', margin: '12px 20px 0', padding: '10px 14px', borderRadius: 14, background: 'rgba(249,115,22,.12)', border: '1px solid rgba(249,115,22,.3)', color: '#FDBA74', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
          <IconCalendar size={18} /><span style={{ flex: 1 }}>Asked for a table: {requestLine(conv.reservation_request)}{conv.reservation_request.note ? ` · ${conv.reservation_request.note}` : ''}</span>
        </div>
      )}
      <div ref={listRef} className="c29-scroll" aria-live="polite" style={{ flex: 1, minHeight: 0, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.map((m) => <Message key={m.id} m={m} />)}
      </div>
      <div style={{ flex: 'none', padding: 16, borderTop: `1px solid ${C.line}`, display: 'flex', gap: 10, alignItems: 'flex-end', background: C.bar }}>
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={2000} rows={2}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={conv.status === 'closed' ? 'Write to open this chat again' : `Reply to ${conv.guest_name}`} aria-label="Reply"
          style={{ flex: 1, minHeight: 52, maxHeight: 140, resize: 'none', borderRadius: 14, border: `1px solid ${C.line2}`, background: C.panel2, color: C.textHi, padding: '12px 14px', fontSize: 15, lineHeight: 1.4, fontFamily: 'inherit' }} />
        <Btn kind="orange" busy={busy} disabled={!draft.trim()} onClick={send} icon={<IconSend size={18} />}>Send</Btn>
      </div>
      <BookModal open={booking} onClose={() => setBooking(false)} conversation={conv} onBooked={() => { live.reload(); onChanged(); }} />
    </div>
  );
}

export default function ChatScreen() {
  const { me, perms } = useDiningSession();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState('open');
  const [soundOn, toggleSound] = useSoundPref();
  const now = useNow(30000);
  const selected = Number(params.get('c')) || null;
  // a new guest message on another chat chimes through the alerts (chat_message), as on every screen
  const list = useLive(() => api.get('/dining/chat/conversations', { status }), [status], { types: CHAT_EVENTS });
  const alertsState = useAlerts({ enabled: alertsEnabledFor(me) });
  const [tray, setTray] = useState(false);
  const open = useCallback((id) => {
    const next = new URLSearchParams(params);
    if (id) next.set('c', String(id)); else next.delete('c');
    setParams(next, { replace: true });
  }, [params, setParams]);
  const rows = list.data?.conversations || [];
  const unread = rows.reduce((n, c) => n + (c.unread ? 1 : 0), 0);

  return (
    <div className="c29 c29-dark" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <DarkTopBar title="Guest chat" sub={[me?.restaurant_name, unread ? `${unread} waiting` : 'All answered'].filter(Boolean).join(' · ')}
        right={(
          <>
            <LiveIndicator status={list.streamStatus} />
            <IconBtn dark label={soundOn ? 'Mute the chime' : 'Turn the chime on'} onClick={toggleSound}>{soundOn ? <IconSoundOn size={20} /> : <IconSoundOff size={20} />}</IconBtn>
            {alertsState.enabled && <AlertBell count={alertsState.alerts.length} onClick={() => setTray(true)} />}
            <UserMenu me={me} />
          </>
        )} />
      <StreamBanner status={list.streamStatus} onRetry={list.reload} />
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <aside style={{ width: 360, maxWidth: '42vw', flex: 'none', borderRight: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column', background: C.panel }}>
          <div style={{ padding: 12, borderBottom: `1px solid ${C.line}` }}>
            <Segmented dark value={status} onChange={setStatus} height={38}
              options={[{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }, { value: 'all', label: 'All' }]} />
          </div>
          <div className="c29-scroll" style={{ flex: 1, padding: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {list.error && <ErrorNote error={errorText(list.error)} onRetry={list.reload} compact />}
            {!list.data && list.loading && <Loading />}
            {list.data && !rows.length && (
              <Empty icon={<IconChat size={28} />} title={status === 'open' ? 'No open chats' : 'Nothing here'}
                body="Guests write from your restaurant's page, online ordering and reservation pages. Their messages land here and chime on every screen of someone who answers chat." />
            )}
            {rows.map((c) => <Row key={c.id} c={c} on={c.id === selected} onOpen={open} now={now} />)}
          </div>
        </aside>
        <section style={{ flex: 1, minWidth: 0, display: 'flex', background: C.floor }}>
          {selected
            ? <Thread key={selected} id={selected} canBook={perms.can('chat.reply')} soundOn={soundOn} onChanged={list.reload} />
            : <Empty icon={<IconChat size={28} />} title="Pick a chat" body="Answer guests, book the table they ask for, close the chat when it is done." style={{ flex: 1 }} />}
        </section>
      </div>
      <BottomNav me={me} active="chat" />
      <AlertCenter me={me} state={alertsState} trayOpen={tray} onTray={setTray} soundOn={soundOn} streamStatus={list.streamStatus} />
    </div>
  );
}
